<ng-container>
  <!-- Back Link Section -->
  @if (showBackLink) {
    <app-back-link
      class="back-link"
      location="top"
      [navigationExtras]="{ scrollToTop: true }"
      [firstInstance]="true">
    </app-back-link>
  }

  <!-- Header Section -->
  <div class="manage-inventory-header" id="manage-header" aria-hidden="false">
    <h1
      id="main-content-focus"
      class="manage-inventory-header__heading pagination-focus d-md-inline-block"
      tabindex="-1"
      role="alert">
      {{ translations.textManageInventoryHeader }}
    </h1>
  </div>

  <!-- Action Links and Search Container -->
  <div class="search-container" style="position: relative;">
    <!-- Action Links -->
    <div class="action_links" [class.tabs-visible]="isActionLinksEnabled">
      @if (hasPreferencesMembers$ | async) {
        <ng-container *ngTemplateOutlet="preferences"></ng-container>
      }

      <!-- Multi VIN Modal Button -->
      <button
        type="button"
        class="manage-inventory-header__multi-vin-btn text-primary d-block d-md-inline-block btn p-0 ms-md-auto mb-2"
        data-track-trigger="modal"
        [attr.data-track-name]="translations.multiVinDialogHeadlineMsg"
        [attr.aria-label]="translations.multiVinDialogHeadlineMsg"
        (click)="openMicroModal('inventory-management-multi-vin-modal')">
        <i aria-hidden="true" class="me-1 allycon-search"></i>
        {{ translations.multiVinDialogHeadlineMsg }}
      </button>
    </div>

    <!-- Search Tab -->
    <div class="advanced_search">
      <tabset
        type="tabs"
        #staticTabs
      >
        <tab
          [heading]="translations.textSearchTabLabel"
        >
          <div class="card card--manage-inventory">
            <ng-container *ngTemplateOutlet="tmplSearch"></ng-container>
          </div>
        </tab>
      </tabset>
    </div>
  </div>

  <!-- Search Form Template -->
  <ng-template #tmplSearch>
    @if (isDesktop$ | async; as isDesktopQuery) {
      <form
        [formGroup]="searchForm"
        id="search-form"
        class="w-100"
        allytm-form-tagged="true">

        <!-- Desktop Search Layout -->
        @if (isDesktopQuery.matches) {
          <div class="d-none d-md-flex desktop-search-layout">

            @if (isEmployeeFilterEnabled) {
                <div class="input-container me-4 manage-inventory-header__account-manager">
                  <label class="control-label text-uppercase">
                    {{ translations.labelAccountManager }}
                  </label>
                  <app-account-manager-selection></app-account-manager-selection>
                </div>
            } @else {
            <!-- Dealer Group Selection -->
              @if (hasLeadSellerMembers$ | async) {
                <div class="input-container me-4 manage-inventory-header__dealer-group">
                  <label class="control-label text-uppercase">
                    {{ translations.textGroupDropdownLabel }}
                  </label>
                  <app-dealer-group-selection></app-dealer-group-selection>
                </div>
              }
            }

            <!-- VIN Input -->
            <div class="input-container">
              <label for="search-by-vin" class="text-uppercase" id="search-by-vin__label">
                {{ translations.textSearchByVinLabel }}
              </label>
              <input
                formControlName="vin"
                id="search-by-vin"
                type="text"
                class="italic-placeholder form-control"
                [class.invalid]="searchForm.get('vin')?.errors"
                [placeholder]="translations.textVinInputPlaceholder"
                [appTrack]="{ name: 'Sell VIN Input' }"
                aria-labelledby="search-by-vin__label"
                aria-describedby="search-by-vin__description search-by-vin__error"
                (keydown.enter)="search()" />

              @if (searchForm.get('vin')?.errors) {
                <div
                  class="text-danger"
                  id="search-by-vin__error"
                  role="alert"
                  aria-live="polite"
                  aria-atomic="true">
                  {{ translations.textEnterValidVinMessage }}
                </div>
              }
            </div>

            <span class="or-text">{{ translations.textOr }}</span>

            <!-- Seller Legacy ID Input (Admin Only) -->
            @if (isAdmin) {
              <div class="input-container">
                <label for="search-by-seller-legacy-id" class="text-uppercase" id="search-by-seller-legacy-id__label">
                  {{ translations.textSearchBySellerLegacyIdLabel }}
                </label>
                <input
                  formControlName="sellerLegacyId"
                  id="search-by-seller-legacy-id"
                  type="text"
                  inputmode="numeric"
                  pattern="^[0-9]{6}$"
                  maxlength="6"
                  class="italic-placeholder form-control"
                  [class.invalid]="searchForm.get('sellerLegacyId')?.errors"
                  [placeholder]="translations.textSellerLegacyIdInputPlaceholder"
                  [appTrack]="{ name: 'Sell SellerLegacyId Input' }"
                  aria-labelledby="search-by-seller-legacy-id__label"
                  aria-describedby="search-by-seller-legacy-id__description search-by-seller-legacy-id__error"
                  data-test-id="app-manage-header-e-textbox-seller-legacy-id" />

                @if (searchForm.get('sellerLegacyId')?.errors) {
                  <div
                    class="text-danger"
                    id="search-by-seller-legacy-id__error"
                    role="alert"
                    aria-live="polite"
                    aria-atomic="true"
                    data-test-id="app-manage-header-e-error-text-seller-legacy-id">
                    {{ translations.textEnterValidSellerLegacyIdMessage }}
                  </div>
                }
              </div>

              <span
                class="or-text"
                data-test-id="app-manage-header-e-or-text-seller-legacy-id">
                {{ translations.textOr }}
              </span>
            }

            <!-- Stock Number Input -->
            <div class="input-container">
              <label for="search-by-stock-number" class="text-uppercase" id="search-by-stock-number__label">
                {{ translations.textSearchByStockNumberLabel }}
              </label>
              <input
                formControlName="stockNum"
                id="search-by-stock-number"
                type="text"
                class="form-control"
                [appTrack]="{ name: 'Sell Stock # Input' }"
                aria-labelledby="search-by-stock-number__label"
                (keydown.enter)="search()" />
            </div>

            <!-- Search Button -->
            <button
              type="button"
              class="btn btn-primary search-btn search_btn"
              data-track-elem="button"
              [attr.data-track-name]="searchTrackName$ | async"
              [attr.aria-label]="translations.textSearchButtonLabel"
              allytmln="searchButton"
              (click)="search()">
              <span class="search-btn__inner">{{ translations.textSearchButtonLabel }}</span>
              <i class="allycon-glyph-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        } @else {
          <!-- Mobile Search Layout -->
          <div class="d-md-none mobile-search-layout">

            @if (isEmployeeFilterEnabled) {
                <div class="input-container">
                  <label class="control-label text-uppercase mb-0">
                    {{ translations.labelAccountManager }}
                  </label>
                  <app-account-manager-selection></app-account-manager-selection>
                </div>
            } @else {
              <!-- Mobile Dealer Group Selection -->
              @if (hasLeadSellerMembers$ | async) {
                <div class="input-container">
                  <label class="control-label text-uppercase mb-0">
                    {{ translations.textGroupDropdownLabel }}
                  </label>
                  <app-dealer-group-selection></app-dealer-group-selection>
                </div>
              }
            }

            <!-- Mobile Search Mode Selection -->
            <div class="form-group">
              <div class="search-by">
                <h2 class="search-by__heading">{{ translations.textSearchByLabel }}</h2>
                <fieldset class="control-wrapper radios">
                  <legend class="visually-hidden">Search options</legend>

                  <!-- VIN Radio -->
                  <div class="custom-control custom-radio custom-control-inline">
                    <input
                      class="custom-control-input"
                      id="mobile-search-vin-radio"
                      name="mobile-search-radios"
                      type="radio"
                      value="vin"
                      [checked]="mobileSearchMode$() === mobileSearchModeEnum.VIN"
                      (change)="setMobileSearchMode(mobileSearchModeEnum.VIN)" />
                    <label class="custom-control-label" for="mobile-search-vin-radio">
                      {{ translations.textVinLabel }}
                    </label>
                  </div>

                  <!-- Seller Legacy ID Radio (Admin Only) -->
                  @if (isAdmin) {
                    <div
                      class="custom-control custom-radio custom-control-inline"
                      data-test-id="app-manage-header-e-mobile-radio-seller-legacy-id">
                      <input
                        class="custom-control-input"
                        id="mobile-search-seller-legacy-id-radio"
                        name="mobile-search-radios"
                        type="radio"
                        value="sellerLegacyId"
                        [checked]="mobileSearchMode$() === mobileSearchModeEnum.SELLERLEGACYID"
                        (change)="setMobileSearchMode(mobileSearchModeEnum.SELLERLEGACYID)" />
                      <label
                        class="custom-control-label"
                        for="mobile-search-seller-legacy-id-radio"
                        data-test-id="app-manage-header-e-mobile-radio-label-seller-legacy-id">
                        {{ translations.textSellerLegacyIdLabel }}
                      </label>
                    </div>
                  }

                  <!-- Stock Number Radio -->
                  <div class="custom-control custom-radio custom-control-inline">
                    <input
                      class="custom-control-input"
                      id="mobile-search-stock-radio"
                      name="mobile-search-radios"
                      type="radio"
                      value="stock"
                      [checked]="mobileSearchMode$() === mobileSearchModeEnum.STOCKNUMBER"
                      (change)="setMobileSearchMode(mobileSearchModeEnum.STOCKNUMBER)" />
                    <label class="custom-control-label" for="mobile-search-stock-radio">
                      {{ translations.textStockUnitLabel }}
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>

            <!-- Mobile Input Container -->
            <div class="input-container">
              <!-- Mobile VIN Input -->
              @if (mobileSearchMode$() === mobileSearchModeEnum.VIN) {
                <input
                  formControlName="vin"
                  id="mobile-search-vin"
                  type="text"
                  placeholder="Enter full VIN or last 6 digits"
                  class="italic-placeholder form-control"
                  [class.invalid]="searchForm.get('vin')?.errors"
                  [attr.aria-label]="translations.textSearchByVinLabel"
                  aria-describedby="search-by-vin__description mobile-search-vin__error"
                  (keydown.enter)="search()" />

                @if (searchForm.get('vin')?.errors) {
                  <div
                    class="text-danger"
                    id="mobile-search-vin__error"
                    role="alert"
                    aria-live="polite"
                    aria-atomic="true">
                    {{ translations.textEnterValidVinMessage }}
                  </div>
                }
              }

              <!-- Mobile Seller Legacy ID Input -->
              @if (mobileSearchMode$() === mobileSearchModeEnum.SELLERLEGACYID) {
                <input
                  formControlName="sellerLegacyId"
                  id="mobile-search-seller-legacy-id"
                  type="text"
                  inputmode="numeric"
                  pattern="^[0-9]{6}$"
                  maxlength="6"
                  class="italic-placeholder form-control"
                  [class.invalid]="searchForm.get('sellerLegacyId')?.errors"
                  [placeholder]="translations.textSellerLegacyIdInputPlaceholder"
                  [attr.aria-label]="translations.textSearchBySellerLegacyIdLabel"
                  aria-describedby="search-by-seller-legacy-id__description mobile-search-seller-legacy-id__error"
                  data-test-id="app-manage-header-e-mobile-input-seller-legacy-id" />

                @if (searchForm.get('sellerLegacyId')?.errors) {
                  <div
                    class="text-danger"
                    id="mobile-search-seller-legacy-id__error"
                    role="alert"
                    aria-live="polite"
                    aria-atomic="true"
                    data-test-id="app-manage-header-e-mobile-mobile-error-text-seller-legacy-id">
                    {{ translations.textEnterValidSellerLegacyIdMessage }}
                  </div>
                }
              }

              <!-- Mobile Stock Number Input -->
              @if (mobileSearchMode$() === mobileSearchModeEnum.STOCKNUMBER) {
                <input
                  formControlName="stockNum"
                  id="mobile-search-stock"
                  type="text"
                  class="italic-placeholder form-control"
                  [attr.aria-label]="translations.textAriaLabelStockNumber"
                  (keydown.enter)="search()" />
              }
            </div>

            <!-- Mobile Search Button -->
            <button
              type="button"
              class="btn btn-primary btn-wide search_btn"
              data-track-elem="button"
              [attr.data-track-name]="searchTrackName$ | async"
              [attr.aria-label]="translations.textSearchButtonLabel"
              (click)="search()">
              {{ translations.textSearchButtonLabel }}
            </button>
          </div>
        }
      </form>
    }
  </ng-template>

  <!-- Preferences Template -->
  <ng-template #preferences>
    <trap-focus [enableFocusTrap]="!disableFocus && pop.isOpen" [enableArrowKeys]="!disableFocus">
      <button
        type="button"
        class="manage-inventory-header__open-edit-preferences btn btn-link d-md-inline-block p-0"
        #pop="bs-popover"
        [popover]="editPreferenceTemplateNew"
        placement="bottom left"
        [adaptivePosition]="false"
        [outsideClick]="true"
        containerClass="manage-preferences-popover"
        data-track-elem="button"
        data-track-name="Edit Preferences"
        (onShown)="onShowPreferencesPop()"
        (onHidden)="onHidePreferencesPop()">
        <i class="me-1 allycon-settings" aria-hidden="true"></i>
        {{ translations.textEditPreference }}
      </button>

      <ng-template #editPreferenceTemplateNew>
        <div class="p-3 preferences">
          <div class="d-flex justify-content-between">
            <div class="me-5 fw-bold align-self-center preferences__title">
              {{ translations.textDefaultPreferences }}
            </div>
          </div>

          <div class="preferences__body my-3">
            <app-preferences-popover-2></app-preferences-popover-2>
          </div>

          <div class="preferences__action">
            <button
              type="button"
              class="btn btn-outline-primary w-100 preferences-btn"
              data-track-elem="button"
              data-track-name="Preferences - close"
              data-track-trigger="Save Preferences"
              (click)="savePreferences()">
              {{ translations.textSavePreferences }}
            </button>

            <button
              type="button"
              class="edit-preferences__close"
              id="ed_close_x_button"
              data-track-elem="button"
              data-track-name="Close"
              data-track-trigger="close"
              [attr.aria-label]="translations.textButtonClose"
              (click)="$event.preventDefault(); pop.hide()">
              <i class="allycon-x text-primary tenant-icons" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </ng-template>
    </trap-focus>
  </ng-template>

  <!-- Modals -->
  <app-multi-vin-search-modal
    #multiVinModal>
  </app-multi-vin-search-modal>
</ng-container>
